
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import PerceptiveSystemIcon from "@/components/journal/PerceptiveSystemIcon";
import RpgPerceptiveSystemIcon from "@/components/character/RpgPerceptiveSystemIcon";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";
import { getRpgThemeClasses } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { ScrollText } from "lucide-react";

interface JournalEntryContentProps {
  journalEntry: {
    dice_value: number;
    stuck_point?: string | null;
    perception_response?: string | null;
    intention_response?: string | null;
    next_step_response?: string | null;
    created_at: string;
    updated_at?: string;
  };
  currentPrompts?: {
    perception: string;
    intention: string;
    nextStep: string;
  };
  entryId?: string;
}

const JournalEntryContent: React.FC<JournalEntryContentProps> = ({
  journalEntry,
  currentPrompts,
  entryId
}) => {
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMMM d, yyyy 'at' h:mm a");
    } catch (e) {
      return "Invalid date";
    }
  };

  // Format the entry ID for RPG style
  const formatEntryId = (id: string) => {
    if (!enableRpgMode) return null;
    // Take the first 8 chars of the UUID to make it shorter
    const shortId = id.split('-')[0];
    return `Quest #${shortId}`;
  };

  return (
    <Card className={cn(
      "mb-6",
      enableRpgMode && rpgTheme.cardBackground + " shadow-xl"
    )}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {enableRpgMode ? (
              <RpgPerceptiveSystemIcon diceValue={journalEntry.dice_value} />
            ) : (
              <PerceptiveSystemIcon diceValue={journalEntry.dice_value} />
            )}
            <div>
              <CardTitle className={cn(
                enableRpgMode && rpgTheme.text
              )}>
                {enableRpgMode ? "Quest Record" : "Journal Entry"}
              </CardTitle>
              <CardDescription className={cn(
                enableRpgMode && rpgTheme.accent
              )}>
                {journalEntry.created_at && `${enableRpgMode ? "Scribed" : "Created"} ${formatDate(journalEntry.created_at)}`}
                {journalEntry.updated_at && journalEntry.updated_at !== journalEntry.created_at && (
                  <span className="ml-2">
                    ({enableRpgMode ? "Amended" : "Updated"} {formatDate(journalEntry.updated_at)})
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
          
          {/* Show entry ID on mobile */}
          {entryId && enableRpgMode && (
            <div className={cn(
              "md:hidden flex items-center gap-1 px-2 py-1 rounded-md",
              "bg-amber-900/40 border border-amber-600/50 text-amber-200",
              "font-medium text-xs"
            )}>
              <ScrollText className="h-3 w-3" />
              {formatEntryId(entryId)}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className={cn(
            "text-lg font-medium mb-2",
            enableRpgMode && rpgTheme.text
          )}>
            {enableRpgMode ? "What challenge do you face?" : "What are you currently stuck on?"}
          </h3>
          <div className={cn(
            "p-4 rounded-md",
            enableRpgMode 
              ? "bg-amber-950/30 border border-amber-900/50" 
              : "bg-muted/30"
          )}>
            <p className={cn(
              enableRpgMode && rpgTheme.text
            )}>
              {journalEntry.stuck_point || (enableRpgMode ? "No challenge specified" : "Not specified")}
            </p>
          </div>
        </div>

        {currentPrompts && (
          <>
            <div>
              <h3 className={cn(
                "text-lg font-medium mb-2",
                enableRpgMode && rpgTheme.text
              )}>
                {currentPrompts.perception}
              </h3>
              <div className={cn(
                "p-4 rounded-md",
                enableRpgMode 
                  ? "bg-amber-950/30 border border-amber-900/50" 
                  : "bg-muted/30"
              )}>
                <p className={cn(
                  enableRpgMode && rpgTheme.text
                )}>
                  {journalEntry.perception_response || (enableRpgMode ? "No observations recorded" : "No response")}
                </p>
              </div>
            </div>
            
            <div>
              <h3 className={cn(
                "text-lg font-medium mb-2",
                enableRpgMode && rpgTheme.text
              )}>
                {currentPrompts.intention}
              </h3>
              <div className={cn(
                "p-4 rounded-md",
                enableRpgMode 
                  ? "bg-amber-950/30 border border-amber-900/50" 
                  : "bg-muted/30"
              )}>
                <p className={cn(
                  enableRpgMode && rpgTheme.text
                )}>
                  {journalEntry.intention_response || (enableRpgMode ? "No intentions declared" : "No response")}
                </p>
              </div>
            </div>
            
            <div>
              <h3 className={cn(
                "text-lg font-medium mb-2",
                enableRpgMode && rpgTheme.text
              )}>
                {currentPrompts.nextStep}
              </h3>
              <div className={cn(
                "p-4 rounded-md",
                enableRpgMode 
                  ? "bg-amber-950/30 border border-amber-900/50" 
                  : "bg-muted/30"
              )}>
                <p className={cn(
                  enableRpgMode && rpgTheme.text
                )}>
                  {journalEntry.next_step_response || (enableRpgMode ? "No next steps planned" : "No response")}
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default JournalEntryContent;
